import { from } from 'rxjs';
import { filter } from 'rxjs/operators';
import { clearKnock, createSources } from '@shoflo/studio-redux/actions';
import { ACCEPTED, PRESENTER } from '@shoflo/polymer';
import { getBroadcastRouteMatch } from '@shoflo/studio-redux/utils';
import { selectLastPresenterSource } from '@shoflo/studio-redux/selectors';
import { BROADCAST_APPROVE_ALL_KNOCKS } from '@shoflo/studio-redux/actions/types';
import { shouldHaveFeature } from '@studio/feature.flags/featureFlags';
import {
  getApiClient,
  createAttendees,
  sendMeetingToAttendees,
  handleFailedCreateAttendees,
  getPresenterSourcesToCreate
} from '../utils';
import { DEFAULT_ORDER_NUMBER } from 'utils/source';
import { VENDOR_AGORA } from '@studio/constants/Vendor';

/**
 * Emits an event to the accepted Presenter socket
 * BulkAcceptedKnock
 *
 * @param socket
 */
export function bulkAcceptedKnock(socket) {
  return store => next => async action => {
    const targetedActions = [`${BROADCAST_APPROVE_ALL_KNOCKS}_SUCCESS`];
    from([action])
      .pipe(filter($action => targetedActions.includes($action.type)))
      .subscribe(async $action => {
        const useAgora =
          shouldHaveFeature('studio.meetingVendor') === VENDOR_AGORA;

        // If using Agora, skip this middleware
        if (useAgora) return next(action);
        // Get all presenter requests and the accepted ones
        const approvedPresenterRequests = $action.payload.filter(
          request => request.type === PRESENTER && request.status === ACCEPTED
        );

        const [firstApprovedPresenterRequest] = approvedPresenterRequests;
        const broadcastId = firstApprovedPresenterRequest?.broadcastId;

        const requestPayload = approvedPresenterRequests.map(req => {
          return {
            userType: 'Presenter',
            presenterId: req.presenterId,
            sessionId: req.sessionId,
            presenterName: req.name
          };
        });

        const apiClient = getApiClient();
        let {
          meeting,
          attendeeRoster,
          program,
          team,
          attendeeCameraStatus,
          broadcastSettings,
          banners,
          streamEndpoints
        } = store.getState();

        const match = getBroadcastRouteMatch(store.getState);

        const { teamId, eventId } = match.params;

        // Check to see if the meeting is in session and that there are acceptedKnocks
        if (!meeting.meetingSession) return;

        // Clear the knock from state
        store.dispatch(clearKnock($action.payload));

        if (!requestPayload?.length) return;

        let createMeetingAttendeesResponse;

        try {
          createMeetingAttendeesResponse = await createAttendees(
            apiClient,
            teamId,
            eventId,
            broadcastId,
            meeting,
            requestPayload
          );
        } catch (e) {
          handleFailedCreateAttendees(
            e,
            store,
            approvedPresenterRequests,
            socket
          );
        }

        if (!createMeetingAttendeesResponse) return;

        sendMeetingToAttendees({
          store,
          socket,
          approvedPresenterRequests,
          createMeetingAttendeesResponse,
          attendeeCameraStatus,
          banners,
          streamEndpoints,
          meeting,
          program,
          attendeeRoster,
          broadcastSettings,
          broadcastId,
          team
        });

        const { order } = selectLastPresenterSource(store.getState());

        /** Bulk create presenter sources from approved requests */
        const presentersSources = getPresenterSourcesToCreate({
          teamId,
          eventId,
          broadcastId,
          approvedPresenterRequests,
          createMeetingAttendeesResponse,
          lastPresenterOrder: order || DEFAULT_ORDER_NUMBER
        });

        store.dispatch(createSources(presentersSources));
      });
    return next(action);
  };
}

export default bulkAcceptedKnock;
